import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';

import type { IColumn, TTheme } from '@feathr/components';
import { Pill, TableColumnHeader } from '@feathr/components';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';

interface IBuildPillColumnOptions<TItem, TValue extends string> {
  accessor: keyof TItem & string;
  labelMap: Record<TValue, string>;
  t: TFunction;
  themeMap: Record<TValue, TTheme>;
  title: string;
  width?: number;
}

/**
 * Returns a column definition that renders a status-style enum value as a
 * translated Pill. Used across the prototype's table views to avoid repeating
 * the same column shape for every status / role / engagement field.
 */
function buildPillColumn<TItem extends object, TValue extends string>({
  accessor,
  labelMap,
  t,
  themeMap,
  title,
  width = 140,
}: IBuildPillColumnOptions<TItem, TValue>): IColumn<TItem> {
  return {
    accessor,
    id: accessor,
    Header: TableColumnHeader({ title, sortType: 'alpha' }),
    headerClassName: tableStyles.sort,
    className: tableStyles.cell,
    sortable: true,
    width,
    Cell({ original }): JSX.Element {
      const value = (original as Record<string, unknown>)[accessor] as TValue;
      return <Pill theme={themeMap[value]}>{t(labelMap[value])}</Pill>;
    },
  };
}

export default buildPillColumn;
